/**
 * Centre de notifications — panneau ouvert par la cloche.
 *
 * - Affiche les conversations avec messages non lus (polling 5s)
 * - Bouton pour activer les notifications Web Push
 * - Toast in-app quand un nouveau message arrive (l'utilisateur voit un flash)
 * - Son court à chaque nouveau message (respect Notification.permission)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, BellOff, Check, X, MessageSquare, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useListerConversationsQuery } from '../../services/messagerieChat';
import { usePushBackoffice } from '../../hooks/usePushBackoffice';
import { openChat } from '../../store/slices/openChatsSlice';

interface Props {
  onNouveauMessage?: (nb: number) => void;
}

// Bip synthétisé via Web Audio API (marche sans fichier audio).
// Deux tons courts : 880 Hz puis 1320 Hz — signature "notif" agréable.
let _audioCtx: AudioContext | null = null;
function jouerBipNotif() {
  try {
    if (!_audioCtx) {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      _audioCtx = new Ctx();
    }
    if (!_audioCtx) return;
    if (_audioCtx.state === 'suspended') _audioCtx.resume().catch(() => {});
    const now = _audioCtx.currentTime;
    const bip = (freq: number, start: number, dur: number, vol: number) => {
      const osc = _audioCtx!.createOscillator();
      const gain = _audioCtx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(vol, now + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      osc.connect(gain);
      gain.connect(_audioCtx!.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };
    bip(880, 0, 0.12, 0.25);
    bip(1320, 0.13, 0.18, 0.20);
  } catch { /* ignore */ }
}

export const NotificationCenter: React.FC<Props> = ({ onNouveauMessage }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [totalPrec, setTotalPrec] = useState<number | null>(null);
  const [flash, setFlash] = useState<{ titre: string; body: string } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titreOriginal = useRef<string>('');
  const clignoteTimer = useRef<any>(null);

  // Poll relâché à 20s (au lieu de 5s) — fix 2026-08-25 : NotificationCenter
  // + Sidebar + MessagerieFloating polaient tous /conversations/ à 5-10s =
  // 3× la charge chat sur les workers. RTK Query mutualise le cache donc les
  // 3 composants partagent la même requête (déduplication automatique).
  const { data, refetch } = useListerConversationsQuery(undefined, {
    pollingInterval: 20_000,
  });
  const conversations = data?.results || [];
  const totalNonLus = conversations.reduce((s, c) => s + (c.nb_non_lus || 0), 0);
  const convsAvecNonLus = conversations.filter(c => c.nb_non_lus > 0);

  // Sauvegarde du titre d'origine + cleanup
  useEffect(() => {
    if (!titreOriginal.current) titreOriginal.current = document.title;
    return () => {
      if (clignoteTimer.current) clearInterval(clignoteTimer.current);
      if (titreOriginal.current) document.title = titreOriginal.current;
    };
  }, []);

  // Clignotement du titre d'onglet quand messages non lus + onglet caché
  useEffect(() => {
    if (clignoteTimer.current) { clearInterval(clignoteTimer.current); clignoteTimer.current = null; }
    if (totalNonLus > 0 && document.visibilityState !== 'visible') {
      let flip = false;
      clignoteTimer.current = setInterval(() => {
        flip = !flip;
        document.title = flip
          ? `🔴 (${totalNonLus}) Nouveau message`
          : titreOriginal.current || document.title;
      }, 1200);
    } else if (titreOriginal.current) {
      document.title = titreOriginal.current;
    }
    return () => {
      if (clignoteTimer.current) { clearInterval(clignoteTimer.current); clignoteTimer.current = null; }
    };
  }, [totalNonLus]);

  // Restaure titre quand l'onglet redevient visible
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && titreOriginal.current) {
        document.title = titreOriginal.current;
        if (clignoteTimer.current) { clearInterval(clignoteTimer.current); clignoteTimer.current = null; }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const push = usePushBackoffice();

  // Détecte les nouveaux messages : toast + son + notif native si onglet caché
  useEffect(() => {
    if (totalPrec === null) { setTotalPrec(totalNonLus); return; }
    if (totalNonLus > totalPrec) {
      const cible = convsAvecNonLus.sort((a, b) =>
        new Date(b.date_derniere_activite).getTime() - new Date(a.date_derniere_activite).getTime()
      )[0];
      if (cible?.dernier_message) {
        const titre = cible.titre_affichage;
        const body = cible.dernier_message.type_msg === 'image' ? '📷 Image'
          : cible.dernier_message.type_msg === 'fichier' ? `📎 ${cible.dernier_message.fichier_nom || 'Fichier'}`
          : cible.dernier_message.contenu.slice(0, 100);

        // 1) Toast in-app (visible dans l'onglet)
        setFlash({ titre, body });

        // 2) Son de notif (Web Audio synth) — marche même sans fichier
        jouerBipNotif();

        // 3) Notification navigateur native — SEULEMENT si l'onglet n'est pas
        //    focus (sinon toast + son suffisent). La Web Push (SW) prend le
        //    relais quand l'app est totalement fermée.
        const tabPasFocus = document.visibilityState !== 'visible';
        if (tabPasFocus && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            const notif = new Notification(`Nouveau message · ${titre}`, {
              body,
              icon: '/logo192.png',
              badge: '/logo192.png',
              tag: `conv-${cible.id}`,
              renotify: true,
              requireInteraction: false,
            } as any);
            notif.onclick = () => {
              window.focus();
              navigate(`/messagerie?conv=${cible.id}`);
              notif.close();
            };
            // Auto-close 6s
            setTimeout(() => { try { notif.close(); } catch {} }, 6000);
          } catch { /* certains navigateurs privés bloquent */ }
        }

        onNouveauMessage?.(totalNonLus - totalPrec);
        setTimeout(() => setFlash(null), 6000);
      }
    }
    setTotalPrec(totalNonLus);
  }, [totalNonLus, convsAvecNonLus, totalPrec, onNouveauMessage, navigate]);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <>
      {/* Bouton cloche */}
      <div className="relative">
        <button
          onClick={() => { setOpen(o => !o); refetch(); }}
          className="relative p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          title="Notifications"
        >
          <Bell size={20} />
          {totalNonLus > 0 && (
            <span className="absolute top-0 right-0 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {totalNonLus > 99 ? '99+' : totalNonLus}
            </span>
          )}
        </button>

        {open && (
          <div ref={panelRef}
            className="absolute right-0 top-full mt-1 w-96 max-h-[500px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                <Bell size={14} /> Notifications
                {totalNonLus > 0 && (
                  <span className="text-xs font-normal text-red-500">({totalNonLus} non lus)</span>
                )}
              </div>
              <button onClick={() => setOpen(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X size={14} />
              </button>
            </div>

            {/* Push activation */}
            {push.supported && !push.enabled && push.permission !== 'denied' && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/60">
                <div className="flex items-start gap-2">
                  <Bell size={14} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1 text-xs text-amber-800 dark:text-amber-200">
                    <div className="font-semibold mb-1">Activez les notifications</div>
                    <div className="text-[11px] text-amber-700 dark:text-amber-300 mb-2">
                      Recevez une alerte système même quand l'application est fermée.
                    </div>
                    <button onClick={() => push.enable()} disabled={push.loading}
                      className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded font-semibold flex items-center gap-1">
                      {push.loading ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
                      Activer
                    </button>
                  </div>
                </div>
              </div>
            )}
            {push.enabled && (
              <div className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-[11px] text-emerald-700 dark:text-emerald-300 border-b border-emerald-200 dark:border-emerald-800/60 flex items-center gap-1.5">
                <Check size={11} /> Notifications système activées
                <button onClick={() => push.disable()} className="ml-auto text-emerald-600 hover:text-emerald-800 underline">
                  Désactiver
                </button>
              </div>
            )}
            {push.permission === 'denied' && (
              <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 text-[11px] text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800/60 flex items-start gap-1.5">
                <BellOff size={11} className="mt-0.5" />
                <span>Notifications bloquées. Réautorisez-les dans les paramètres du navigateur (icône 🔒 à côté de l'URL).</span>
              </div>
            )}

            {/* Liste convs non lues */}
            <div className="flex-1 overflow-y-auto">
              {convsAvecNonLus.length === 0 ? (
                <div className="p-6 text-center text-xs text-gray-500">
                  Aucune nouvelle notification.
                </div>
              ) : convsAvecNonLus.map(c => (
                <button
                  key={c.id}
                  onClick={() => {
                    // Ouvre un mini-drawer (style FB Messenger) au lieu de naviguer.
                    // Le drawer est rendu par ChatDrawersStack dans MainLayout.
                    dispatch(openChat({ convId: c.id, label: c.titre_affichage }));
                    setOpen(false);
                  }}
                  className="w-full flex items-start gap-2.5 px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 ${
                    c.type_conv === 'broadcast' ? 'bg-red-500' :
                    c.type_conv === 'service' ? 'bg-blue-500' :
                    c.type_conv === 'direct' ? 'bg-slate-400' : 'bg-emerald-500'
                  }`}>
                    {c.titre_affichage.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                        {c.titre_affichage}
                      </div>
                      <span className="bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 min-w-[16px] text-center shrink-0">
                        {c.nb_non_lus}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 truncate mt-0.5">
                      {c.dernier_message ? (
                        c.dernier_message.type_msg === 'image' ? '📷 Image'
                          : c.dernier_message.type_msg === 'fichier' ? `📎 ${c.dernier_message.fichier_nom || 'Fichier'}`
                          : c.dernier_message.contenu
                      ) : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Footer */}
            <button
              onClick={() => { navigate('/messagerie'); setOpen(false); }}
              className="p-2.5 border-t border-gray-200 dark:border-gray-700 text-xs text-amber-600 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center gap-1.5"
            >
              <MessageSquare size={12} /> Ouvrir la messagerie
              <ExternalLink size={10} />
            </button>
          </div>
        )}
      </div>

      {/* Toast flash in-app — bulle en haut à droite pendant 6s
          Design ostensible : bordure amber épaisse, avatar animé, pulse subtile */}
      {flash && (
        <div className="fixed top-16 right-4 z-[100] max-w-sm"
          style={{ animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <div className="relative">
            {/* Halo pulsé derrière */}
            <div className="absolute inset-0 rounded-lg bg-amber-500/30 animate-ping" style={{ animationDuration: '2s' }} />
            <button
              onClick={() => { navigate('/messagerie'); setFlash(null); }}
              className="relative w-full bg-white dark:bg-gray-800 border-2 border-amber-500 rounded-lg shadow-2xl p-3 flex items-start gap-3 text-left hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-lg">
                <MessageSquare size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-amber-600 font-bold">
                  Nouveau message
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white truncate mt-0.5">
                  {flash.titre}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                  {flash.body}
                </div>
                <div className="text-[10px] text-amber-600 mt-1 font-medium">
                  Cliquez pour ouvrir →
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setFlash(null); }}
                className="text-gray-400 hover:text-gray-600 shrink-0 p-1">
                <X size={14} />
              </button>
            </button>
          </div>
        </div>
      )}
      {/* Keyframes animation slide */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};
